import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated } from 'react-native';
import { useReprocessFundingsMutation } from '@/store/api';
import { IFundingTableRow, IProcessedFunding, BulkReprocessHandler } from '@/types/admin/funding';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS } from '@/constants/admin';
import { useSpinAnimation } from '@/hooks/common/useSpinAnimation';


interface FundingMassActionsProps {
  selectedRows: IFundingTableRow[];
  onClose: () => void;
  onReprocessComplete: BulkReprocessHandler;
  onDownload?: () => void;
}

export const FundingMassActions: React.FC<FundingMassActionsProps> = ({
  selectedRows,
  onClose,
  onReprocessComplete,
  onDownload,
}) => {
  const { showSuccess, showError } = useToast();
  const adminUser = useAdminUser();
  
  const [reprocessFundings, { isLoading: isReprocessLoading }] = useReprocessFundingsMutation();
  const spin = useSpinAnimation(isReprocessLoading);

  const handleBulkReprocess = async () => {
    if (!adminUser.selectedOrganizationId) {
      showError('No organization selected');
      return;
    }

    try {
      const fundingIds = selectedRows.map((row) => row.id);
            
      const response = await reprocessFundings({
        ids: fundingIds,
        organizationId: adminUser.selectedOrganizationId,
      }).unwrap();

      // Update the rows with new data from the response
      if (response && response.response) {
        onReprocessComplete(response.response);
      }
      
      showSuccess('Reprocessing completed successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to reprocess fundings:', error);
      showError('Failed to reprocess fundings. Please try again.');
    }
  };

  return (
    <Animated.View style={styles.container}>
      <View style={styles.panel}>
        <View style={styles.content}>
          {/* Selection Count */}
          <View style={styles.selectionInfo}>
            <Text style={styles.selectionText}>
              {selectedRows.length} selected
            </Text>
          </View>

          {/* Action Buttons */}
          <View style={styles.actionsContainer}>
            {/* Reprocess Button */}
            <TouchableOpacity
              style={[styles.actionButton, styles.reprocessButton]}
              onPress={handleBulkReprocess}
              disabled={isReprocessLoading}
            >
              <View style={styles.buttonContent}>
                <Animated.View style={{ transform: [{ rotate: spin }] }}>
                  <SVG_COMPONENTS.REFRESH 
                    width={16} 
                    height={16} 
                    color={ADMIN_COLORS.PRIMARY} 
                  />
                </Animated.View>
                <Text style={styles.reprocessButtonText}>Reprocess</Text>
              </View>
            </TouchableOpacity>

            {/* Download Button (only if provided) */}
            {onDownload && (
              <TouchableOpacity
                style={[styles.actionButton, styles.downloadButton]}
                onPress={onDownload}
                disabled={false}
              >
                <View style={styles.buttonContent}>
                  <SVG_COMPONENTS.DOWNLOAD 
                    width={16} 
                    height={16} 
                    color={ADMIN_COLORS.TEXT_BLACK}
                  />
                  <Text style={styles.downloadButtonText}>Download</Text>
                </View>
              </TouchableOpacity>
            )}



            {/* Close Button */}
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
            >
              <SVG_COMPONENTS.CANCEL_X 
                width={16} 
                height={16} 
                color={ADMIN_COLORS.TEXT_TERTIARY} 
              />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    zIndex: 1000,
  },
  panel: {
    backgroundColor: '#2D2D2D', // Dark gray for mass actions panel
    borderRadius: 12,
    borderTopWidth: 3,
    borderTopColor: ADMIN_COLORS.PRIMARY,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  selectionInfo: {
    flex: 1,
  },
  selectionText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  actionButton: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    minWidth: 100,
  },
  reprocessButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  downloadButton: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderWidth: 0,
  },

  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  reprocessButtonText: {
    color: ADMIN_COLORS.PRIMARY,
    fontSize: 14,
    fontWeight: '600',
  },
  downloadButtonText: {
    color: ADMIN_COLORS.TEXT_BLACK,
    fontSize: 14,
    fontWeight: '600',
  },

  closeButton: {
    padding: 8,
  },
});
